import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowLeft,
    Flame,
    Plus,
    Trash2,
    Link as LinkIcon,
    CalendarClock,
    DollarSign,
    Users,
    Pencil,
    X,
    Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ClientModal from "@/components/ClientModal";
import StaffAssignmentDialog from "@/components/StaffAssignmentDialog";
import { generateQuotePdf } from "@/lib/generateQuotePdf";
import { generatePaymentReceiptPdf } from "@/lib/generateReceiptPdf";

const STAGES = ["Enquiry", "InProgress", "PostProduction", "Completed"];
const STAGE_LABEL = {
    Enquiry: "Enquiry",
    InProgress: "In Progress",
    PostProduction: "Post-Production",
    Completed: "Completed",
};

function formatDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return d;
    }
}

export default function ClientDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [client, setClient] = useState(null);
    const [comments, setComments] = useState([]);
    const [payments, setPayments] = useState([]);
    const [profit, setProfit] = useState(null);
    const [stageComment, setStageComment] = useState("");
    const [selectedStage, setSelectedStage] = useState("Enquiry");
    const [editOpen, setEditOpen] = useState(false);
    const [assignFor, setAssignFor] = useState(null); // { event }'
    const [isSubmittingPay, setIsSubmittingPay] = useState(false);
    const [payForm, setPayForm] = useState({
        amount_received: "",
        date_of_payment: new Date().toISOString().slice(0, 10),
        payment_mode: "Cash",
        note: "",
    });
    const [expenseForm, setExpenseForm] = useState({ title: "", amount: "" });
    const [editingEvent, setEditingEvent] = useState(null); // { idx, event } for links editor

    const load = async () => {
        try {
            const [c, cmts, pays, pro] = await Promise.all([
                api.get(`/clients/${id}`),
                api.get(`/clients/${id}/comments`),
                api.get(`/clients/${id}/payments`),
                api.get(`/clients/${id}/profitability`),
            ]);
            setClient(c.data);
            setSelectedStage(c.data.current_stage);
            setComments(cmts.data);
            setPayments(pays.data);
            setProfit(pro.data);
        } catch (e) {
            toast.error("Failed to load client");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const totalReceived = useMemo(
        () => payments.reduce((s, p) => s + (p.amount_received || 0), 0),
        [payments]
    );

    const totalClientExpenses = useMemo(
        () => (client?.expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0),
        [client]
    );

    if (!client) return <div className="p-4 sm:p-8 text-sm text-gray-500">Loading…</div>;

    const updateStage = async () => {
        try {
            await api.patch(`/clients/${id}/stage`, {
                current_stage: selectedStage,
                comment: stageComment || undefined,
            });
            setStageComment("");
            toast.success("Stage updated");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const addPayment = async () => {
    if (!payForm.amount_received) return toast.error("Enter an amount");
    
    setIsSubmittingPay(true); // 🔒 Disable button
    try {
        const response = await api.post(`/clients/${id}/payments`, {
            amount_received: Number(payForm.amount_received),
            date_of_payment: payForm.date_of_payment,
            payment_mode: payForm.payment_mode,
            note: payForm.note || undefined,
        });

        const newPaymentObj = response.data || {
            id: Date.now().toString(),
            amount_received: Number(payForm.amount_received),
            date_of_payment: payForm.date_of_payment,
            payment_mode: payForm.payment_mode,
            note: payForm.note,
        };

        const updatedPayments = [newPaymentObj, ...payments];

        setPayForm({ ...payForm, amount_received: "", note: "" });
        toast.success("Payment recorded");

        await load();

        // Auto-generate PDF Receipt on successful addition
        generatePaymentReceiptPdf(
            client,
            newPaymentObj,
            updatedPayments,
            user?.brandName || "Studio"
        );
    } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
    } finally {
        setIsSubmittingPay(false); // 🔓 Re-enable button
    }
};

    const removePayment = async (pid) => {
        try {
            await api.delete(`/payments/${pid}`);
            await load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const addClientExpense = async () => {
        if (!expenseForm.title.trim() || !expenseForm.amount) {
            return toast.error("Enter an expense title and amount");
        }

        try {
            const currentExpenses = client.expenses || [];
            const newExpense = {
                id: Date.now().toString(),
                title: expenseForm.title.trim(),
                amount: Number(expenseForm.amount),
                created_at: new Date().toISOString(),
            };

            await api.patch(`/clients/${id}`, {
                expenses: [...currentExpenses, newExpense],
            });

            setExpenseForm({ title: "", amount: "" });
            toast.success("Expense recorded");
            await load();
        } catch (e) {
            toast.error("Failed to record expense");
        }
    };

    const removeClientExpense = async (expenseId) => {
        try {
            const updatedExpenses = (client.expenses || []).filter((e) => e.id !== expenseId);
            await api.patch(`/clients/${id}`, { expenses: updatedExpenses });
            toast.success("Expense deleted");
            await load();
        } catch (e) {
            toast.error("Failed to delete expense");
        }
    };

    const unassign = async (event, assignmentId) => {
        try {
            await api.delete(`/clients/${id}/events/${event.id}/assign/${assignmentId}`);
            await load();
        } catch {
            toast.error("Failed");
        }
    };

    const saveEventLinks = async (evt, newLinks) => {
        try {
            await api.put(`/clients/${id}/events/${evt.id}`, { ...evt, links: newLinks });
            toast.success("Links saved");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    // Calculate Net Profit factoring in staff cost + common expenses
    const netProfitMargin = profit ? profit.margin - totalClientExpenses : 0;
    const netMarginPct = profit && profit.revenue > 0 ? (netProfitMargin / profit.revenue) * 100 : 0;

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto" data-testid="client-details-page">
            {/* Back + Header */}
            <button
                onClick={() => navigate("/")}
                className="text-xs sm:text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-4"
                data-testid="back-to-dashboard"
            >
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Back to dashboard
            </button>

            <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 sm:mb-8 gap-4">
                <div>
                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-1.5">
                        {client.package?.album_type} · {client.package?.album_pages} pages
                    </div>
                    <h1 className="font-serif text-2xl sm:text-4xl md:text-5xl text-gray-900 tracking-tight flex flex-wrap items-center gap-2 sm:gap-3">
                        <span>{client.name}</span>
                        {client.is_fast_track && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
                                <Flame className="w-3 h-3" /> Fast Track
                            </span>
                        )}
                    </h1>
                    <div className="text-xs sm:text-sm text-gray-500 mt-2">
                        Expected delivery{" "}
                        <span className="tabular-nums text-gray-800 font-medium">{formatDate(client.expected_delivery_date)}</span>{" "}
                        · {client.events.length} event{client.events.length !== 1 && "s"}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateQuotePdf(client, user?.brandName || "Studio")}
                        data-testid="download-quote-pdf"
                        className="self-start sm:self-auto shrink-0"
                    >
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download Quote
                    </Button>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditOpen(true)}
                        data-testid="edit-client-details"
                        className="self-start sm:self-auto shrink-0"
                    >
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit client
                    </Button>
                </div>
            </div>

            {/* Roadmap */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6" data-testid="roadmap-section">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400">Project Roadmap</div>
                        <h2 className="font-serif text-lg sm:text-xl text-gray-900 mt-0.5">Update stage & log a comment</h2>
                    </div>
                </div>

                {/* Stepper */}
                <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-2 no-scrollbar">
                    {STAGES.map((s, i) => {
                        const active = client.current_stage === s;
                        const passed = STAGES.indexOf(client.current_stage) > i;
                        return (
                            <React.Fragment key={s}>
                                <div
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 ${
                                        active
                                            ? "bg-blue-500 text-white"
                                            : passed
                                            ? "bg-blue-50 text-blue-700"
                                            : "bg-gray-100 text-gray-500"
                                    }`}
                                >
                                    <div
                                        className={`w-1.5 h-1.5 rounded-full ${
                                            active ? "bg-white" : passed ? "bg-blue-600" : "bg-gray-400"
                                        }`}
                                    ></div>
                                    {STAGE_LABEL[s]}
                                </div>
                                {i < STAGES.length - 1 && (
                                    <div className={`h-px w-4 sm:w-6 shrink-0 ${passed ? "bg-blue-300" : "bg-gray-200"}`}></div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                        <Label className="text-xs sm:text-sm">Set current stage</Label>
                        <Select value={selectedStage} onValueChange={setSelectedStage}>
                            <SelectTrigger className="mt-1" data-testid="roadmap-stage-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STAGES.map((s) => (
                                    <SelectItem key={s} value={s}>
                                        {STAGE_LABEL[s]}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Label className="text-xs sm:text-sm">Add a comment about this change</Label>
                        <Textarea
                            value={stageComment}
                            onChange={(e) => setStageComment(e.target.value)}
                            rows={2}
                            placeholder="Editor assigned, first cut expected next week…"
                            className="mt-1 text-xs sm:text-sm"
                            data-testid="roadmap-comment-input"
                        />
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                        <Button
                            onClick={updateStage}
                            size="sm"
                            className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600"
                            data-testid="roadmap-save-button"
                        >
                            Save stage & comment
                        </Button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="w-full">
                <div className="overflow-x-auto no-scrollbar border-b border-gray-200 mb-6">
                    <TabsList className="bg-transparent rounded-none p-0 gap-4 sm:gap-6 h-auto w-full justify-start flex-nowrap whitespace-nowrap">
                        <TabsTrigger
                            value="overview"
                            data-testid="tab-overview"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 pb-3 px-1 text-xs sm:text-sm bg-transparent"
                        >
                            Overview
                        </TabsTrigger>
                        <TabsTrigger
                            value="events"
                            data-testid="tab-events-team"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 pb-3 px-1 text-xs sm:text-sm bg-transparent"
                        >
                            Events & Team ({client.events.length})
                        </TabsTrigger>
                        <TabsTrigger
                            value="payments"
                            data-testid="tab-payments"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 pb-3 px-1 text-xs sm:text-sm bg-transparent"
                        >
                            Payments ({payments.length})
                        </TabsTrigger>
                    </TabsList>
                </div>

                {/* OVERVIEW TAB */}
                <TabsContent value="overview" className="mt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        
                        {/* Package Details */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-3">Package Details</div>
                            <dl className="space-y-3 text-xs sm:text-sm">
                                <Row label="Album" value={`${client.package?.album_type} · ${client.package?.album_pages} pages`} />
                                <Row label="Total committed" value={`₹${(client.package?.total_committed_amount || 0).toLocaleString()}`} mono />
                                <Row label="Delivery" value={formatDate(client.expected_delivery_date)} mono />
                                <Row label="Fast Track" value={client.is_fast_track ? "Yes" : "No"} />
                                <div>
                                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mt-4 mb-2">Package Deliverables</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {(client.package?.addons || []).length === 0 ? (
                                            <div className="text-xs text-gray-400">No deliverables specified</div>
                                        ) : (
                                            client.package.addons.map((a) => (
                                                <span key={a} className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">
                                                    {a}
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </dl>
                        </div>

                        {/* Contact & Profitability */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-3">Contact Information</div>
                            <dl className="space-y-3 text-xs sm:text-sm">
                                <Row label="Email" value={client.contact_email || "—"} />
                                <Row label="Phone" value={client.contact_phone || "—"} />
                            </dl>
                            {profit && (
                                <>
                                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mt-6 mb-3">
                                        Profitability Overview
                                    </div>
                                    <dl className="space-y-3 text-xs sm:text-sm">
                                        <Row label="Revenue" value={`₹${profit.revenue.toLocaleString()}`} mono />
                                        <Row label="Staff Expenses" value={`₹${(profit.staff_cost || 0).toLocaleString()}`} mono />
                                        <Row label="Common Expenses (Album, etc.)" value={`₹${totalClientExpenses.toLocaleString()}`} mono />
                                        <Row
                                            label="Net Margin"
                                            value={`₹${netProfitMargin.toLocaleString()} (${netMarginPct.toFixed(1)}%)`}
                                            mono
                                            accent={netProfitMargin >= 0 ? "emerald" : "red"}
                                        />
                                    </dl>
                                </>
                            )}
                        </div>

                        {/* Project Expenses Manager */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400">
                                    Common Project Expenses
                                </div>
                                <div className="text-xs font-semibold tabular-nums text-gray-900">
                                    Total: ₹{totalClientExpenses.toLocaleString()}
                                </div>
                            </div>

                            <div className="space-y-2 max-h-40 overflow-y-auto mb-4 pr-1">
                                {(client.expenses || []).length === 0 ? (
                                    <div className="text-xs text-gray-400 italic">No expenses logged (e.g. Album print, frame, courier).</div>
                                ) : (
                                    client.expenses.map((e) => (
                                        <div key={e.id} className="flex items-center justify-between p-2 text-xs bg-gray-50 border border-gray-100 rounded-md">
                                            <div>
                                                <div className="font-medium text-gray-800">{e.title}</div>
                                                <div className="text-[10px] text-gray-400">{formatDate(e.created_at)}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold tabular-nums text-gray-900">₹{Number(e.amount).toLocaleString()}</span>
                                                <button
                                                    onClick={() => removeClientExpense(e.id)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-0.5"
                                                    title="Delete expense"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="space-y-2 pt-2 border-t border-gray-100">
                                <Label className="text-xs font-medium text-gray-700">Add Common Expense</Label>
                                <div className="grid grid-cols-5 gap-2">
                                    <Input
                                        placeholder="Album print, Courier..."
                                        value={expenseForm.title}
                                        onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })}
                                        className="col-span-3 text-xs"
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        placeholder="Amount (₹)"
                                        value={expenseForm.amount}
                                        onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                                        className="col-span-2 text-xs tabular-nums"
                                    />
                                </div>
                                <Button
                                    onClick={addClientExpense}
                                    size="sm"
                                    variant="outline"
                                    className="w-full text-xs mt-1 border-dashed"
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Expense
                                </Button>
                            </div>
                        </div>

                        {/* Comment Log */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 md:col-span-2 lg:col-span-3">
                            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-3">Comment Log</div>
                            {comments.length === 0 ? (
                                <div className="text-xs sm:text-sm text-gray-400">No comments logged yet.</div>
                            ) : (
                                <ol className="space-y-3 max-h-60 overflow-y-auto pr-1">
                                    {comments.map((c) => (
                                        <li key={c.id} className="flex gap-2.5">
                                            <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></div>
                                            <div>
                                                <div className="text-xs sm:text-sm text-gray-800 leading-snug">{c.comment}</div>
                                                <div className="text-[10px] sm:text-[11px] text-gray-400 mt-0.5 tabular-nums">
                                                    {c.stage_on_comment ? `${STAGE_LABEL[c.stage_on_comment]} · ` : ""}
                                                    {formatDate(c.created_at)}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>

                    </div>
                </TabsContent>

                {/* EVENTS & TEAM */}
                <TabsContent value="events" className="mt-0 space-y-4 sm:space-y-5">
                    {client.events.map((ev) => (
                        <EventCard
                            key={ev.id}
                            event={ev}
                            onAssign={() => setAssignFor(ev)}
                            onUnassign={(aid) => unassign(ev, aid)}
                            onEditLinks={() => setEditingEvent(ev)}
                        />
                    ))}
                </TabsContent>

                {/* PAYMENTS TAB */}
                <TabsContent value="payments" className="mt-0">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <div className="p-4 sm:p-6 border-b border-gray-100">
                                <div className="flex items-center justify-between gap-2">
                                    <div>
                                        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400">Ledger</div>
                                        <h3 className="font-serif text-lg sm:text-xl mt-0.5">Payment History</h3>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-widest text-gray-400">Total Received</div>
                                        <div className="font-serif text-xl sm:text-2xl tabular-nums text-emerald-600 font-medium">
                                            ₹{totalReceived.toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500"
                                        style={{
                                            width: `${Math.min(
                                                100,
                                                (totalReceived / Math.max(1, client.package?.total_committed_amount || 1)) * 100
                                            )}%`,
                                        }}
                                    ></div>
                                </div>
                                <div className="mt-1.5 text-xs text-gray-500 tabular-nums">
                                    ₹{totalReceived.toLocaleString()} of ₹{(client.package?.total_committed_amount || 0).toLocaleString()} received
                                </div>
                            </div>

                            <div className="divide-y divide-gray-100" data-testid="payments-list">
                                {payments.length === 0 ? (
                                    <div className="p-4 sm:p-6 text-xs sm:text-sm text-gray-400">No payments recorded yet.</div>
                                ) : (
                                    payments.map((p) => (
                                        <div key={p.id} className="flex items-center justify-between px-4 sm:px-6 py-3">
                                            <div>
                                                <div className="text-xs sm:text-sm font-medium tabular-nums text-gray-900">
                                                    ₹{p.amount_received.toLocaleString()}
                                                </div>
                                                <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                                                    {formatDate(p.date_of_payment)} · {p.payment_mode}
                                                    {p.note ? ` · ${p.note}` : ""}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() =>
                                                        generatePaymentReceiptPdf(
                                                            client,
                                                            p,
                                                            payments,
                                                            user?.brandName || "Studio"
                                                        )
                                                    }
                                                    className="text-xs text-blue-600 hover:text-blue-800 p-1 flex items-center gap-1"
                                                    title="Download Receipt PDF"
                                                >
                                                    <Download className="w-3.5 h-3.5" />
                                                    <span className="hidden sm:inline">Receipt</span>
                                                </button>

                                                <button
                                                    onClick={() => removePayment(p.id)}
                                                    className="text-gray-400 hover:text-red-600 p-1"
                                                    data-testid={`delete-payment-${p.id}`}
                                                    title="Delete payment entry"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-3">Record Advance / Payment</div>
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-xs sm:text-sm">Amount (₹)</Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={payForm.amount_received}
                                        onChange={(e) => setPayForm({ ...payForm, amount_received: e.target.value })}
                                        data-testid="pay-amount-input"
                                        className="mt-1 tabular-nums"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs sm:text-sm">Date</Label>
                                    <Input
                                        type="date"
                                        value={payForm.date_of_payment}
                                        onChange={(e) => setPayForm({ ...payForm, date_of_payment: e.target.value })}
                                        data-testid="pay-date-input"
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs sm:text-sm">Payment Mode</Label>
                                    <Select
                                        value={payForm.payment_mode}
                                        onValueChange={(v) => setPayForm({ ...payForm, payment_mode: v })}
                                    >
                                        <SelectTrigger className="mt-1" data-testid="pay-mode-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Cash">Cash</SelectItem>
                                            <SelectItem value="Card">Card</SelectItem>
                                            <SelectItem value="BankTransfer">Bank Transfer</SelectItem>
                                            <SelectItem value="UPI">UPI</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs sm:text-sm">Note (optional)</Label>
                                    <Input
                                        value={payForm.note}
                                        onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                                        className="mt-1 text-xs sm:text-sm"
                                        data-testid="pay-note-input"
                                        placeholder="Advance, second installment…"
                                    />
                                </div>
                               <Button
    onClick={addPayment}
    disabled={isSubmittingPay}
    className="w-full bg-blue-500 hover:bg-blue-600 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
    data-testid="add-payment-button"
>
    {isSubmittingPay ? (
        <span className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Recording...
        </span>
    ) : (
        <>
            <Plus className="w-4 h-4 mr-1.5" /> Add payment
        </>
    )}
</Button>
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            <ClientModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={() => {
                    setEditOpen(false);
                    load();
                }}
                initial={client}
            />

            <StaffAssignmentDialog
                open={!!assignFor}
                event={assignFor}
                clientId={id}
                onClose={() => setAssignFor(null)}
                onSaved={() => {
                    setAssignFor(null);
                    load();
                }}
            />

            <LinksEditor
                open={!!editingEvent}
                event={editingEvent}
                onClose={() => setEditingEvent(null)}
                onSave={async (links) => {
                    await saveEventLinks(editingEvent, links);
                    setEditingEvent(null);
                }}
            />
        </div>
    );
}

function Row({ label, value, mono, accent }) {
    const color =
        accent === "emerald"
            ? "text-emerald-600 font-medium"
            : accent === "red"
            ? "text-red-600 font-medium"
            : "text-gray-900";
    return (
        <div className="flex items-center justify-between gap-2">
            <dt className="text-gray-500">{label}</dt>
            <dd className={`${mono ? "tabular-nums" : ""} ${color} truncate text-right`}>{value}</dd>
        </div>
    );
}

function EventCard({ event, onAssign, onUnassign, onEditLinks }) {
    const photog = event.assignments.filter((a) => a.role === "Photographer").length;
    const video = event.assignments.filter((a) => a.role === "Videographer").length;
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6" data-testid={`event-card-${event.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 gap-3">
                <div>
                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400">Event</div>
                    <h3 className="font-serif text-xl sm:text-2xl text-gray-900 mt-0.5">{event.event_name}</h3>
                    <div className="text-xs sm:text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-2 sm:gap-3">
                        <span className="inline-flex items-center gap-1 tabular-nums">
                            <CalendarClock className="w-3.5 h-3.5" /> {new Date(event.event_date).toLocaleDateString()}
                        </span>
                        <span>·</span>
                        <span>
                            {photog}/{event.needed_photographers} photog · {video}/{event.needed_videographers} video
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={onEditLinks} data-testid={`event-links-${event.id}`} className="text-xs">
                        <LinkIcon className="w-3.5 h-3.5 sm:mr-1.5" />
                        <span className="hidden sm:inline">Links</span>
                    </Button>
                    <Button size="sm" onClick={onAssign} className="bg-blue-500 hover:bg-blue-600 text-xs" data-testid={`event-assign-${event.id}`}>
                        <Users className="w-3.5 h-3.5 sm:mr-1.5" />
                        <span>Assign staff</span>
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-2">Team Assigned</div>
                    {event.assignments.length === 0 ? (
                        <div className="text-xs sm:text-sm text-gray-400">No staff assigned to this event yet.</div>
                    ) : (
                        <ul className="space-y-2">
                            {event.assignments.map((a) => (
                                <li key={a.id} className="flex items-center justify-between text-xs sm:text-sm border border-gray-100 rounded-md px-3 py-2 bg-gray-50/50">
                                    <div>
                                        <div className="text-gray-900 font-medium">{a.staff_name}</div>
                                        <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                                            {a.role} · <span className="tabular-nums">₹{(a.payment_amount || 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => onUnassign(a.id)}
                                        className="text-gray-400 hover:text-red-600 p-1"
                                        data-testid={`unassign-${a.id}`}
                                        title="Unassign staff member"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {(event.addons || []).length > 0 && (
                        <div className="mt-4">
                            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-2">Event Addons</div>
                            <div className="flex flex-wrap gap-1.5">
                                {event.addons.map((a) => (
                                    <span
                                        key={a}
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100"
                                        data-testid={`event-view-addon-${event.id}-${a.replace(/\s+/g, "-").toLowerCase()}`}
                                    >
                                        {a}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400 mb-2">Delivery Links</div>
                    {(event.links || []).length === 0 ? (
                        <div className="text-xs sm:text-sm text-gray-400">
                            No delivery links added yet. Add selection gallery, teasers, or film URLs.
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {event.links.map((l, i) => (
                                <li key={i} className="text-xs sm:text-sm flex items-center gap-2 overflow-hidden">
                                    <LinkIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    <a
                                        href={l.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 hover:underline truncate flex items-center gap-1.5"
                                    >
                                        <span className="text-gray-700 font-medium shrink-0">{l.label}:</span>
                                        <span className="truncate">{l.url}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function LinksEditor({ open, event, onClose, onSave }) {
    const [links, setLinks] = useState([]);
    useEffect(() => {
        if (event) setLinks(event.links?.length ? event.links : [{ label: "Selection Gallery", url: "" }]);
    }, [event]);

    if (!open || !event) return null;

    const preset = ["Selection Gallery", "Final Photos", "Teaser Video", "Full Film"];
    return (
        <div className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={onClose}>
            <div
                className="w-[95vw] sm:max-w-lg bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="links-editor"
            >
                <div className="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
                    <div>
                        <div className="text-[10px] sm:text-xs uppercase tracking-widest text-gray-400">Delivery links</div>
                        <h3 className="font-serif text-lg sm:text-2xl mt-0.5">{event.event_name}</h3>
                    </div>
                    <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-md">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 sm:p-6 space-y-3 overflow-y-auto flex-1">
                    {links.map((l, i) => (
                        <div key={i} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-gray-50/60 p-2.5 sm:p-0 rounded border sm:border-0 border-gray-100">
                            <Select
                                value={l.label}
                                onValueChange={(v) => {
                                    const next = [...links];
                                    next[i] = { ...next[i], label: v };
                                    setLinks(next);
                                }}
                            >
                                <SelectTrigger className="sm:w-2/5 text-xs sm:text-sm bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {preset.map((p) => (
                                        <SelectItem key={p} value={p}>
                                            {p}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input
                                className="flex-1 text-xs sm:text-sm bg-white"
                                placeholder="https://…"
                                value={l.url}
                                onChange={(e) => {
                                    const next = [...links];
                                    next[i] = { ...next[i], url: e.target.value };
                                    setLinks(next);
                                }}
                                data-testid={`link-url-${i}`}
                            />
                            <button
                                onClick={() => setLinks(links.filter((_, ix) => ix !== i))}
                                className="text-xs text-red-500 hover:underline self-end sm:self-center shrink-0 px-1 py-0.5"
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <Button
                        variant="outline"
                        onClick={() => setLinks([...links, { label: "Selection Gallery", url: "" }])}
                        className="w-full border-dashed text-xs sm:text-sm mt-2"
                        data-testid="add-link-row"
                    >
                        <Plus className="w-4 h-4 mr-1.5" /> Add another link
                    </Button>
                </div>
                <div className="p-3.5 sm:p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                        onClick={() => onSave(links.filter((l) => l.url.trim()))}
                        data-testid="save-links-button"
                    >
                        Save links
                    </Button>
                </div>
            </div>
        </div>
    );
}